/**
 * Drawer with editable role metadata + tool-grant matrix.
 *
 * The matrix is a checkbox per available tool (filtered to the role's
 * workspace). "Save" PATCHes /roles/{id} with the new tool list.
 */
import { useEffect, useMemo, useState } from "react";

import { adminApi } from "../../api/admin";
import { Drawer } from "../../components/admin/Drawer";
import { useToast } from "../../components/toast/context";
import { EditableField } from "../../components/EditableField";
import { QueryBoundary } from "../../components/QueryBoundary";
import { Skeleton } from "../../components/Skeleton";

interface Props {
  roleId: string | null;
  onClose: () => void;
}

export function RoleEditDrawer({ roleId, onClose }: Props) {
  const open = !!roleId;
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Role detail"
      eyebrow="Admin"
      widthClass="w-[680px] max-w-full"
    >
      {roleId ? <DrawerBody roleId={roleId} /> : null}
    </Drawer>
  );
}

function DrawerBody({ roleId }: { roleId: string }) {
  const roleQuery = adminApi.roles.get(roleId);
  const toolsQuery = adminApi.tools.list();
  const patchMutation = adminApi.roles.patch(roleId);
  const toast = useToast();

  const [draftTools, setDraftTools] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (roleQuery.data) {
      setDraftTools(new Set(roleQuery.data.tool_names));
      setDirty(false);
    }
  }, [roleQuery.data]);

  const allTools = toolsQuery.data ?? [];
  const visibleTools = useMemo(() => {
    if (!roleQuery.data) return [];
    const ws = roleQuery.data.workspace_slug;
    return allTools.filter((t) => t.workspaces.includes(ws));
  }, [allTools, roleQuery.data]);

  const toggle = (name: string) => {
    setDraftTools((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
    setDirty(true);
  };

  const save = () => {
    patchMutation.mutate(
      { tool_names: Array.from(draftTools) },
      {
        onSuccess: () => {
          toast.ok("Role updated");
          setDirty(false);
        },
        onError: (e) => toast.error(e),
      },
    );
  };

  return (
    <>
      <QueryBoundary
        query={roleQuery}
        loading={
          <div className="space-y-3">
            <Skeleton height={28} width="60%" />
            <Skeleton height={20} width="40%" />
            <Skeleton height={120} width="100%" />
          </div>
        }
      >
        {(role) => (
          <>
            <div className="mb-5">
              <div className="font-heading text-xl font-semibold text-ink">
                {role.name}
              </div>
              <div className="text-sm text-ink-low mt-1">
                Workspace: <span className="tag-chip">{role.workspace_slug}</span>
              </div>
            </div>

            <section className="panel panel--flush mb-5">
              <div className="panel-header">
                <h3>Profile</h3>
              </div>
              <div
                className="panel-body grid gap-4"
                style={{ gridTemplateColumns: "minmax(120px, 0.4fr) 1fr" }}
              >
                <FieldLabel>Name</FieldLabel>
                <EditableField
                  value={role.name}
                  label="Role name"
                  onSave={async (next) => {
                    if (next === role.name) return;
                    await patchMutation.mutateAsync({ name: next });
                    toast.ok("Renamed");
                  }}
                />
                <FieldLabel>Description</FieldLabel>
                <EditableField
                  value={role.description ?? ""}
                  label="Description"
                  variant="textarea"
                  onSave={async (next) =>
                    patchMutation.mutateAsync({ description: next || null })
                  }
                />
              </div>
            </section>

            <section className="panel panel--flush mb-5">
              <div className="panel-header">
                <h3>Tool grants</h3>
                <span className="text-xs text-ink-low">
                  {draftTools.size}/{visibleTools.length} selected
                </span>
              </div>
              <div className="panel-body">
                {visibleTools.length === 0 ? (
                  <span className="text-sm text-ink-low">
                    No tools registered for workspace "{role.workspace_slug}".
                  </span>
                ) : (
                  <ToolMatrix
                    tools={visibleTools}
                    selected={draftTools}
                    onToggle={toggle}
                  />
                )}
              </div>
              {dirty ? (
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      setDraftTools(new Set(role.tool_names));
                      setDirty(false);
                    }}
                  >
                    Discard
                  </button>
                  <button
                    type="button"
                    className="btn btn-main"
                    disabled={patchMutation.isPending}
                    onClick={save}
                  >
                    {patchMutation.isPending ? "Saving…" : "Save grants"}
                  </button>
                </div>
              ) : null}
            </section>
          </>
        )}
      </QueryBoundary>
    </>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-heading font-semibold text-ink-low pt-2">
      {children}
    </span>
  );
}

function ToolMatrix({
  tools,
  selected,
  onToggle,
}: {
  tools: { name: string; category: string }[];
  selected: Set<string>;
  onToggle: (name: string) => void;
}) {
  const grouped = useMemo(() => {
    const map = new Map<string, { name: string; category: string }[]>();
    for (const t of tools) {
      const list = map.get(t.category) ?? [];
      list.push(t);
      map.set(t.category, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [tools]);

  return (
    <div className="space-y-4">
      {grouped.map(([category, list]) => (
        <div key={category}>
          <div className="text-xs font-heading font-semibold text-ink-low mb-2">
            {category}
          </div>
          <div className="grid gap-1" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {list.map((tool) => (
              <label
                key={tool.name}
                className="flex items-center gap-2 cursor-pointer text-sm"
              >
                <input
                  type="checkbox"
                  checked={selected.has(tool.name)}
                  onChange={() => onToggle(tool.name)}
                />
                <span className="font-mono">{tool.name}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
